/**
 * Slowpokes theme-native promotional popup.
 * Lightweight, no dependencies. Respects frequency + design mode.
 *
 * Trigger logic (OR): opens on the first met condition among
 * - delay (seconds > 0)
 * - scroll percent (> 0)
 * - desktop exit intent (if enabled)
 * If none are enabled, falls back to a 1s delay.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-promo-popup]');
  if (!root || root.getAttribute('data-promo-popup-bound') === 'true') return;
  root.setAttribute('data-promo-popup-bound', 'true');

  var dialog = root.querySelector('[data-promo-popup-dialog]');
  var overlay = root.querySelector('[data-promo-popup-overlay]');
  var closeBtn = root.querySelector('[data-promo-popup-close]');
  if (!dialog || !closeBtn) return;

  var campaignId = root.getAttribute('data-campaign-id') || 'campaign';
  var delaySeconds = parseInt(root.getAttribute('data-delay') || '5', 10);
  var scrollPercent = parseInt(root.getAttribute('data-scroll-percent') || '0', 10);
  var exitIntent = root.getAttribute('data-exit-intent') === 'true';
  var frequency = root.getAttribute('data-frequency') || '7';
  var storageKey = 'sp_promo_popup_' + campaignId;

  var designMode =
    (window.Shopify && (Shopify.designMode || Shopify.visualPreviewMode)) ||
    false;

  var opened = false;
  var lastFocus = null;
  var delayTimer = null;
  var closedInDesign = false;
  var reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function isDismissed() {
    if (designMode) return closedInDesign;

    if (frequency === 'session') {
      try {
        return sessionStorage.getItem(storageKey) === '1';
      } catch (e) {
        return false;
      }
    }

    var days = parseInt(frequency, 10);
    if (!days || days < 1) days = 7;

    try {
      var raw = localStorage.getItem(storageKey);
      if (!raw) return false;
      var data = JSON.parse(raw);
      if (!data || !data.ts) return false;
      var ms = days * 24 * 60 * 60 * 1000;
      return Date.now() - Number(data.ts) < ms;
    } catch (e) {
      return false;
    }
  }

  function markDismissed() {
    if (designMode) return;

    if (frequency === 'session') {
      try {
        sessionStorage.setItem(storageKey, '1');
      } catch (e) {}
      return;
    }

    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ ts: Date.now(), campaign: campaignId })
      );
    } catch (e) {}
  }

  function getFocusable() {
    return Array.prototype.slice
      .call(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      )
      .filter(function (el) {
        return !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true';
      });
  }

  function openPopup() {
    if (opened || isDismissed()) return;
    opened = true;
    cleanupTriggers();

    lastFocus = document.activeElement;
    root.hidden = false;
    root.classList.add('is-open');
    if (!designMode) {
      document.documentElement.classList.add('sp-promo-popup-open');
    }

    window.requestAnimationFrame(function () {
      root.classList.add('is-visible');
      var focusables = getFocusable();
      if (focusables.length) {
        focusables[0].focus();
      } else {
        dialog.focus();
      }
    });
  }

  function closePopup(persist) {
    if (!opened) return;
    opened = false;
    root.classList.remove('is-visible');
    document.documentElement.classList.remove('sp-promo-popup-open');

    var finish = function () {
      root.hidden = true;
      root.classList.remove('is-open');
      if (designMode) closedInDesign = true;
      if (persist) markDismissed();
      if (lastFocus && typeof lastFocus.focus === 'function') {
        try {
          lastFocus.focus();
        } catch (e) {}
      }
    };

    if (reduceMotion) {
      finish();
    } else {
      window.setTimeout(finish, 180);
    }
  }

  function onKeydown(e) {
    if (!opened) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closePopup(true);
      return;
    }

    if (e.key !== 'Tab') return;

    var focusables = getFocusable();
    if (!focusables.length) {
      e.preventDefault();
      dialog.focus();
      return;
    }

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onScroll() {
    if (opened || scrollPercent <= 0) return;
    var doc = document.documentElement;
    var body = document.body;
    var scrollTop = window.pageYOffset || doc.scrollTop || body.scrollTop || 0;
    var height = Math.max(
      body.scrollHeight,
      doc.scrollHeight,
      body.offsetHeight,
      doc.offsetHeight
    );
    var view = window.innerHeight || doc.clientHeight;
    var maxScroll = Math.max(height - view, 1);
    var pct = (scrollTop / maxScroll) * 100;
    if (pct >= scrollPercent) openPopup();
  }

  function onExitIntent(e) {
    if (!exitIntent || opened) return;
    if (e.clientY > 0) return;
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
    openPopup();
  }

  function cleanupTriggers() {
    if (delayTimer) {
      window.clearTimeout(delayTimer);
      delayTimer = null;
    }
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onExitIntent);
  }

  function setupTriggers() {
    if (isDismissed()) return;

    var useDelay = delaySeconds > 0;
    var useScroll = scrollPercent > 0;
    var useExit = exitIntent === true;

    if (designMode) {
      // Theme Editor: show quickly for preview; ignore frequency.
      delayTimer = window.setTimeout(openPopup, 600);
      return;
    }

    if (!useDelay && !useScroll && !useExit) {
      delayTimer = window.setTimeout(openPopup, 1000);
      return;
    }

    if (useDelay) {
      delayTimer = window.setTimeout(openPopup, delaySeconds * 1000);
    }

    if (useScroll) {
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    if (useExit) {
      document.addEventListener('mouseout', onExitIntent);
    }
  }

  closeBtn.addEventListener('click', function () {
    closePopup(true);
  });

  if (overlay) {
    overlay.addEventListener('click', function () {
      closePopup(true);
    });
  }

  root.querySelectorAll('[data-promo-popup-cta]').forEach(function (el) {
    el.addEventListener('click', function () {
      markDismissed();
    });
  });

  document.addEventListener('keydown', onKeydown);

  setupTriggers();
})();
